import { apiRequest, buildHeaders, buildURL, handleApiError } from './commons/CommonService.Functions';
import * as Functions from '../functions/common';
import * as Sentry from '@sentry/react';

// models
import {
  IdentifierTypeApiError,
  IdentifierTypeCreateCommand,
  IdentifierTypeStatusCommand,
  IdentifierTypeUpdateCommand,
  IdentifierTypeViewModel
} from '../models/Entities/Person/IdentifierType/IdentifierTypeModels';

const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'persons/identifiertypes';

const parseJson = (body: string): any => {
  try {
    return body ? JSON.parse(body) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Error de una respuesta no exitosa (Persons.API spec 005). Parte del `ErrorType` genérico
 * (`HttpErrorHandler`) y le agrega el `message` y el `data` de la envoltura `Response`, que el genérico
 * pierde: el 409 trae ahí el tipo existente o la cantidad de identificadores que usan el tipo.
 */
export const toIdentifierTypeApiError = (status: number, body: string, response: unknown): IdentifierTypeApiError => {
  const base = Functions.HttpErrorHandler(body || null, response);
  const envelope = parseJson(body);
  const isEnvelope = !!envelope && typeof envelope === 'object' && 'succeeded' in envelope;
  const serverMessage: unknown = envelope?.message ?? envelope?.Message;
  return {
    ...base,
    ErrorCode: status || base.ErrorCode,
    ServerMessage: typeof serverMessage === 'string' && serverMessage.trim() ? serverMessage : undefined,
    Data: isEnvelope ? envelope.data ?? envelope.Data : undefined,
    // Persons.API anterior a la spec 005: la ruta no existe (404 sin la envoltura `Response`) o el verbo no (405).
    RouteMissing: status === 405 || (status === 404 && !isEnvelope)
  };
};

/** Como `apiRequest`, pero los errores conservan `message`, `data` y si la ruta no existe. */
const identifierTypesRequest = async <T>(url: string, token: string, context: string, options: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(url, { ...options, headers: buildHeaders(token) });
  } catch (error) {
    throw await handleApiError(error, context);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    const error = toIdentifierTypeApiError(response.status, body, response);
    Sentry.captureException(error);
    throw error;
  }

  if (response.status === 204 || response.headers?.get('Content-Length') === '0') {
    return undefined as unknown as T;
  }
  const data = await response.json();
  // Persons.API anterior a la spec 005 respondía la baja inexistente con 200 y `succeeded: false`.
  if (data && data.succeeded === false) {
    const message = String(data.message ?? 'error inesperado');
    const error: IdentifierTypeApiError = {
      ErrorCode: response.status,
      ErrorMessage: message,
      Errors: Array.isArray(data.errors) ? data.errors.map(String) : [],
      ServerMessage: message,
      Data: data.data
    };
    throw error;
  }
  return data?.data as T;
};

// Get all identifier types
export const GetIdentifierTypes = async (
  token: string,
  includeDeleted: boolean = false
): Promise<IdentifierTypeViewModel[]> => {
  const requestUrl: string = `${BaseURL}?IncludeDeleted=${includeDeleted}`;
  return apiRequest<IdentifierTypeViewModel[]>(requestUrl, token, 'GetIdentifierTypes');
};

// Get identifier type by id
export const GetIdentifierTypeById = async (
  token: string,
  identifierTypeId: number
): Promise<IdentifierTypeViewModel> => {
  const requestUrl: string = buildURL(BaseURL, identifierTypeId);
  return apiRequest<IdentifierTypeViewModel>(requestUrl, token, 'GetIdentifierTypeById');
};

// Create identifier type (409: nombre usado por un tipo vigente, `Data` = el existente)
export const CreateIdentifierType = async (
  token: string,
  payload: IdentifierTypeCreateCommand
): Promise<IdentifierTypeViewModel> => {
  return identifierTypesRequest<IdentifierTypeViewModel>(BaseURL, token, 'CreateIdentifierType', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

// Update identifier type (404: inexistente o dado de baja; 409: nombre usado por otro tipo vigente)
export const UpdateIdentifierType = async (
  token: string,
  payload: IdentifierTypeUpdateCommand
): Promise<IdentifierTypeViewModel> => {
  return identifierTypesRequest<IdentifierTypeViewModel>(BaseURL, token, 'UpdateIdentifierType', {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

// Activate / deactivate: PATCH persons/identifierTypes/{id}/status { isActive } (Persons.API spec 005)
export const ChangeIdentifierTypeStatus = async (
  token: string,
  identifierTypeId: number,
  isActive: boolean
): Promise<IdentifierTypeViewModel> => {
  const requestUrl: string = buildURL(BaseURL, identifierTypeId, 'status');
  const body: IdentifierTypeStatusCommand = { isActive };
  return identifierTypesRequest<IdentifierTypeViewModel>(requestUrl, token, 'ChangeIdentifierTypeStatus', {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

// Delete identifier type (404: inexistente o ya dado de baja; 409: en uso, `Data` = IdentifierTypeInUseViewModel)
export const DeleteIdentifierType = async (
  token: string,
  identifierTypeId: number
): Promise<number> => {
  const requestUrl: string = buildURL(BaseURL, identifierTypeId);
  return identifierTypesRequest<number>(requestUrl, token, 'DeleteIdentifierType', {
    method: 'DELETE'
  });
};
